import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { calcCodiceFiscale, calcAge, COMUNI_LIST } from "@/lib/codiceFiscale";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Pill, FlaskConical, Stethoscope, ClipboardCheck, Repeat, MessageSquarePlus,
  ChevronRight, Send, Save, FileText, ListPlus, User, Check, AlertTriangle, Sparkles, Brain,
  Heart, Wind, Bone, Ear, Utensils, Puzzle, Eye, Activity, Baby, Sprout, Ribbon,
  Zap, Droplets, Beaker, HandMetal, Sun, Feather, Plus,
  Wand2, Lightbulb, History, X,
} from "lucide-react";

/**
 * NUOVA VALUTAZIONE CLINICA — flusso guidato per MMG (Fase 2).
 * Regola fondamentale: il medico inserisce l'informazione UNA SOLA VOLTA
 * e la piattaforma la riutilizza (nella bozza di Sinapsi, nel referto, ecc.).
 */

const REASONS = [
  { code: "farmaci",   label: "Farmaci / prescrizione", icon: Pill,           color: "text-purple-700 bg-purple-50 border-purple-200" },
  { code: "esami",     label: "Esami / accertamenti",   icon: FlaskConical,   color: "text-amber-700 bg-amber-50 border-amber-200" },
  { code: "sintomo",   label: "Sintomo o problema",     icon: Stethoscope,    color: "text-rose-700 bg-rose-50 border-rose-200" },
  { code: "controllo", label: "Controllo",              icon: ClipboardCheck, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  { code: "follow_up", label: "Follow-up",              icon: Repeat,         color: "text-sky-700 bg-sky-50 border-sky-200" },
  { code: "altro",     label: "Altro",                  icon: MessageSquarePlus, color: "text-slate-700 bg-slate-100 border-slate-200" },
];

const PATHWAYS = [
  { code: "manage_direct",  label: "Gestisci direttamente il caso", dotCls: "bg-emerald-500", icon: ClipboardCheck,
    desc: "Continua tu la gestione del paziente. Prescrivi esami, terapia farmacologica e indicazioni non farmacologiche (stile di vita, dieta, presidi)." },
  { code: "create_sinapsi", label: "Crea una Sinapsi con uno specialista", dotCls: "bg-blue-500", icon: Send,
    desc: "Invia il caso a uno specialista. La bozza di richiesta viene compilata automaticamente con i dati già inseriti." },
  { code: "manage_and_sinapsi", label: "Gestisci direttamente E contatta uno specialista", dotCls: "bg-indigo-500", icon: Stethoscope,
    desc: "Doppio percorso: prescrivi esami, terapia e indicazioni al paziente e nello stesso caso apri una Sinapsi al collega." },
];

/** Evaluazione lato client dei red flags per feedback in tempo reale */
function evalRedFlags(moduleData, answersMap) {
  if (!moduleData?.red_flag_rules?.length) return [];
  const byId = {};
  Object.entries(answersMap).forEach(([qid, v]) => {
    if (v?.skipped) return;
    const a = (v?.answer || "").trim().toLowerCase();
    if (a) byId[qid] = a;
  });
  const triggered = [];
  for (const rule of moduleData.red_flag_rules) {
    const min = rule.min_matches || 1;
    let matches = 0;
    for (const c of (rule.conditions || [])) {
      const hay = byId[c.question_id] || "";
      const needle = (c.answer_contains || "").toLowerCase();
      if (needle && hay && hay.includes(needle)) matches += 1;
    }
    if (matches >= min) triggered.push({ label: rule.label, severity: rule.severity || "warning" });
  }
  return triggered;
}

export default function NuovaValutazionePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routeCaseId = searchParams.get("caseId");
  const [savingCase, setSavingCase] = useState(false);
  const [caseId, setCaseId] = useState(null);
  const [caseStatus, setCaseStatus] = useState(null);

  // Paziente
  const [pFirst, setPFirst] = useState("");
  const [pLast, setPLast] = useState("");
  const [pEmail, setPEmail] = useState("");
  const [pCF, setPCF] = useState("");
  const [pCFManual, setPCFManual] = useState(false);
  const [pBirth, setPBirth] = useState("");
  const [pBirthPlace, setPBirthPlace] = useState("");
  const [pBirthProvince, setPBirthProvince] = useState("");
  const [pPhone, setPPhone] = useState("");
  const [pSex, setPSex] = useState("");
  const [provinces, setProvinces] = useState([]);
  const pAge = useMemo(() => calcAge(pBirth), [pBirth]);

  // Auto-calcolo Codice Fiscale
  useEffect(() => {
    if (pCFManual) return;
    const cf = calcCodiceFiscale({
      firstName: pFirst, lastName: pLast, sex: pSex,
      dob: pBirth, birthPlace: pBirthPlace,
    });
    if (cf) setPCF(cf);
  }, [pFirst, pLast, pSex, pBirth, pBirthPlace, pCFManual]);

  useEffect(() => {
    api.get("/contacts/provinces").then(({ data }) => setProvinces(data || [])).catch(() => {});
  }, []);

  // Motivo (multi)
  const [reasonCodes, setReasonCodes] = useState([]);
  const [reasonFree, setReasonFree] = useState("");

  // Moduli sintomo — caricati dinamicamente
  const [modulesList, setModulesList] = useState([]);
  const [symptomModule, setSymptomModule] = useState("");
  const [moduleData, setModuleData] = useState(null);
  const [symptomAnswers, setSymptomAnswers] = useState({});

  // Anamnesi + valutazione
  const [an, setAn] = useState({ patologie: "", allergie: "", farmaci: "", interventi: "", altre: "" });
  const [examination, setExamination] = useState("");
  const [evaluation, setEvaluation] = useState("");

  // Percorso scelto
  const [pathway, setPathway] = useState(null);

  // Percorso: gestione diretta
  const [mgmt, setMgmt] = useState({
    indicazioni: "", terapia: "", esami_richiesti: "",
    indicazioni_non_farmacologiche: "",
    controllo_successivo: "", note: "",
  });

  // AI per indicazioni non farmacologiche (stile di vita, dieta, presidi)
  const [nonPharmaBusy, setNonPharmaBusy] = useState(false);

  // Percorso: Sinapsi
  const [sinapsiSpec, setSinapsiSpec] = useState("");
  const [sinapsiSuggest, setSinapsiSuggest] = useState(null);
  const SpecSuggestHint = ({ testid }) => sinapsiSuggest?.spec ? (
    <div className="mt-1 text-[11px] text-indigo-700 flex items-center gap-1 flex-wrap" data-testid={testid}>
      <Sparkles className="h-3 w-3" />
      {sinapsiSuggest.source === "ai" ? "Sfera suggerita dall'AI" : "Suggerita dal modulo sintomo"}:
      <b>{sinapsiSuggest.spec}</b>
      {sinapsiSuggest.confidence > 0 && <span className="text-slate-500">({sinapsiSuggest.confidence}%)</span>}
      {sinapsiSpec !== sinapsiSuggest.spec && (
        <button type="button" className="underline" onClick={() => { setSinapsiSpec(sinapsiSuggest.spec); loadSpecialistsForSpec(sinapsiSuggest.spec); }}>usa</button>
      )}
    </div>
  ) : null;
  const [sinapsiAdd, setSinapsiAdd] = useState("");
  const [sinapsiDraft, setSinapsiDraft] = useState("");
  const [sinapsiBusy, setSinapsiBusy] = useState(false);
  const [specialists, setSpecialists] = useState([]);
  const [selectedSpecialist, setSelectedSpecialist] = useState(null);
  const [sinapsiPriority, setSinapsiPriority] = useState("normale");
  const [sinapsiUrgency, setSinapsiUrgency] = useState("normale");
  const [sending, setSending] = useState(false);

  // Percorso: esami / prescrizione
  const [examLines, setExamLines] = useState([]);
  const [rxLines, setRxLines] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);

  // AI Diagnosi Differenziale
  const [dxBusy, setDxBusy] = useState(false);
  const [dxItems, setDxItems] = useState([]);

  // Patient CF lookup: se il paziente ha già visite precedenti con lo stesso
  // codice fiscale, mostriamo un banner "Paziente già visitato" con la
  // possibilità di richiamare l'anagrafica + vedere le visite precedenti.
  const [pastVisits, setPastVisits] = useState([]);
  const [pastVisitsSummary, setPastVisitsSummary] = useState("");
  const [pastPatientDismissed, setPastPatientDismissed] = useState(false);
  const [linkedPreviousCaseId, setLinkedPreviousCaseId] = useState(null);

  // AI hint sul testo libero del motivo
  const [hintBusy, setHintBusy] = useState(false);
  const [hint, setHint] = useState(null);

  // AI suggerisci prescrizione
  const [rxAiBusy, setRxAiBusy] = useState(false);
  const [rxAiRationale, setRxAiRationale] = useState("");
  const [rxAiSuggestions, setRxAiSuggestions] = useState([]);

  // Selezione ipotesi di lavoro (dall'AI o "Altro" scritto dal MMG)
  // Ora è un array: il medico può selezionare fino a 3 ipotesi
  // così l'AI suggerisce esami che aiutano a differenziarle.
  const [selectedHyps, setSelectedHyps] = useState([]);
  const [otherHypText, setOtherHypText] = useState("");
  const [otherHypReasoning, setOtherHypReasoning] = useState("");
  const [otherHypResp, setOtherHypResp] = useState(null);
  const [otherHypBusy, setOtherHypBusy] = useState(false);

  // Razionale complessivo AI su esami
  const [examsRationale, setExamsRationale] = useState("");

  const includeSymptom = reasonCodes.includes("sintomo");

  // Carica elenco moduli dal server
  useEffect(() => {
    api.get("/cases/modules").then(({ data }) => setModulesList(data?.modules || []))
       .catch(() => setModulesList([]));
  }, []);

  // Quando il codice fiscale raggiunge 16 caratteri (formato IT) o comunque
  // ne ha ≥ 11, cerchiamo se il paziente è già stato visitato dal medico:
  // in tal caso mostriamo il banner "Paziente già visitato" con l'elenco
  // delle vecchie visite e un pulsante per richiamare l'anagrafica.
  useEffect(() => {
    if (routeCaseId) return;   // in modifica di caso esistente non serve
    const cf = (pCF || "").trim().toUpperCase();
    if (cf.length < 11) {
      setPastVisits([]); setPastVisitsSummary(""); setPastPatientDismissed(false);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get(`/cases/patient-lookup?fiscal_code=${encodeURIComponent(cf)}`);
        const matches = data?.matches || [];
        // Escludi il caso corrente se esiste
        const filtered = caseId ? matches.filter((m) => m.id !== caseId) : matches;
        setPastVisits(filtered);
        setPastVisitsSummary(data?.last_summary || "");
      } catch { /* silenzioso */ }
    }, 600);
    return () => clearTimeout(handle);
  }, [pCF, routeCaseId, caseId]);

  const recallAnagrafica = () => {
    if (pastVisits.length === 0) return;
    const last = pastVisits[0];
    if (last.patient_first_name && !pFirst) setPFirst(last.patient_first_name);
    if (last.patient_last_name && !pLast) setPLast(last.patient_last_name);
    if (last.patient_birth_date && !pBirth) setPBirth(last.patient_birth_date);
    if (last.patient_birth_place && !pBirthPlace) setPBirthPlace(last.patient_birth_place);
    if (last.patient_birth_province && !pBirthProvince) setPBirthProvince(last.patient_birth_province);
    if (last.patient_phone && !pPhone) setPPhone(last.patient_phone);
    if (last.patient_email && !pEmail) setPEmail(last.patient_email);
    if (last.patient_sex && !pSex) setPSex(last.patient_sex);
    toast.success("Anagrafica richiamata dalla visita precedente");
    setPastPatientDismissed(true);
  };

  // Se navighiamo con ?caseId=X (dalla pagina di dettaglio, per correggere dati
  // della visita iniziale), carichiamo il caso e popoliamo tutti i campi.
  useEffect(() => {
    if (!routeCaseId) return;
    (async () => {
      try {
        const { data } = await api.get(`/cases/${routeCaseId}`);
        const c = data.case;
        if (!c) return;
        setCaseId(c.id); setCaseStatus(c.status);
        setPFirst(c.patient_first_name || "");
        setPLast(c.patient_last_name || "");
        setPEmail(c.patient_email || "");
        setPCF(c.patient_fiscal_code || "");
        setPCFManual(!!c.patient_fiscal_code);
        setPBirth(c.patient_birth_date || "");
        setPBirthPlace(c.patient_birth_place || "");
        setPBirthProvince(c.patient_birth_province || "");
        setPPhone(c.patient_phone || "");
        setPSex(c.patient_sex || "");
        setReasonCodes(c.reason_codes || []);
        setReasonFree(c.reason_free_text || "");
        setAn(c.anamnesis || { patologie: "", allergie: "", farmaci: "", interventi: "", altre: "" });
        setExamination(c.examination || "");
        setEvaluation(c.clinical_evaluation || "");
        if (c.symptom_module) {
          setSymptomModule(c.symptom_module);
          try {
            const { data: md } = await api.get(`/cases/modules/${c.symptom_module}`);
            setModuleData(md);
          } catch { /* ignore */ }
          const map = {};
          for (const a of (c.symptom_answers || [])) {
            if (a?.question_id) map[a.question_id] = {
              answer: a.answer || "", note: a.note || "", skipped: !!a.skipped,
            };
          }
          setSymptomAnswers(map);
        }
        toast.info("Dati della visita caricati — puoi correggerli e salvare");
      } catch {
        toast.error("Impossibile caricare il caso per modifica");
      }
    })();
  }, [routeCaseId]);

  useEffect(() => {
    if (!includeSymptom) { setSymptomModule(""); setModuleData(null); setSymptomAnswers({}); }
  }, [includeSymptom]);

  const loadModule = async (id) => {
    if (!id) { setModuleData(null); return; }
    try {
      const { data } = await api.get(`/cases/modules/${id}`);
      setModuleData(data);
      setSymptomAnswers({});
    } catch { toast.error("Impossibile caricare le domande del modulo"); }
  };

  const redFlags = useMemo(
    () => evalRedFlags(moduleData, symptomAnswers),
    [moduleData, symptomAnswers]);

  const toggleReason = (code) => {
    setReasonCodes((prev) => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  const canCreateCase = pFirst.trim() && pLast.trim() && (reasonCodes.length > 0 || reasonFree.trim());

  const buildAnswersPayload = () => {
    if (!moduleData) return [];
    return moduleData.questions.map(q => ({
      question_id: q.id, question_label: q.label,
      answer: (symptomAnswers[q.id]?.answer || ""),
      note: (symptomAnswers[q.id]?.note || ""),
      skipped: !!symptomAnswers[q.id]?.skipped,
    }));
  };

  const createOrUpdateCase = async () => {
    if (!canCreateCase) { toast.error("Inserisci almeno paziente e motivo"); return null; }
    setSavingCase(true);
    try {
      const body = {
        patient_first_name: pFirst.trim(), patient_last_name: pLast.trim(),
        patient_email: pEmail.trim().toLowerCase(),
        patient_fiscal_code: pCF.trim().toUpperCase(),
        patient_birth_date: pBirth,
        patient_birth_place: pBirthPlace.trim(),
        patient_birth_province: pBirthProvince,
        patient_phone: pPhone.trim(),
        patient_sex: pSex,
        reason_codes: reasonCodes, reason_free_text: reasonFree.trim(),
        symptom_module: symptomModule || null, symptom_answers: buildAnswersPayload(),
        anamnesis: an, examination: examination.trim(), clinical_evaluation: evaluation.trim(),
      };
      if (!caseId) {
        const { data } = await api.post("/cases", body);
        setCaseId(data.case_id); setCaseStatus(data.status);
        toast.success("Caso creato");
        return data.case_id;
      } else {
        await api.put(`/cases/${caseId}`, body);
        toast.success("Caso salvato");
        return caseId;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio del caso");
      return null;
    } finally { setSavingCase(false); }
  };

  const choosePathway = async (path) => {
    const id = caseId || await createOrUpdateCase();
    if (!id) return;
    try {
      await api.post(`/cases/${id}/action`, { action: "choose_pathway", pathway: path });
      setPathway(path);
      if (path === "create_sinapsi" || path === "manage_and_sinapsi") await generateSinapsiDraft(id);
    } catch { toast.error("Impossibile impostare il percorso"); }
  };

  const generateSinapsiDraft = async (id = caseId) => {
    if (!id) return;
    setSinapsiBusy(true);
    try {
      const { data } = await api.post(`/cases/${id}/sinapsi-draft`, {
        specialization: sinapsiSpec, additional_notes: sinapsiAdd, priority: sinapsiPriority,
      });
      setSinapsiDraft(data.draft_content || "");
      const suggested = (data.suggested_specialization || "").trim();
      if (suggested) {
        setSinapsiSuggest({ spec: suggested, source: data.suggested_source, confidence: data.suggested_confidence });
        if (!sinapsiSpec) {
          setSinapsiSpec(suggested);
          await loadSpecialistsForSpec(suggested);
        }
      }
      toast.success("Bozza compilata automaticamente dai dati del caso");
    } catch { toast.error("Errore nella generazione della bozza"); }
    finally { setSinapsiBusy(false); }
  };

  const loadSpecialistsForSpec = async (spec) => {
    if (!spec?.trim()) { setSpecialists([]); return; }
    try {
      const { data } = await api.get("/contacts", { params: { specialization: spec.trim(), limit: 50 } });
      setSpecialists(Array.isArray(data) ? data : []);
    } catch { setSpecialists([]); }
  };

  const sendSinapsi = async () => {
    if (!caseId) return;
    if (!selectedSpecialist) { toast.error("Seleziona lo specialista destinatario"); return; }
    if (!sinapsiDraft.trim()) { toast.error("La bozza è vuota"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/send-sinapsi`, {
        specialist_id: selectedSpecialist.id,
        body: sinapsiDraft,
        priority: sinapsiPriority,
        urgency: sinapsiUrgency,
      });
      toast.success("Sinapsi inviata allo specialista");
      navigate(`/casi/${caseId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'invio della Sinapsi");
    } finally { setSending(false); }
  };

  const saveManagementAndClose = async () => {
    const id = caseId || await createOrUpdateCase();
    if (!id) return;
    try {
      await api.put(`/cases/${id}`, {
        management: mgmt,
        exam_requests: examLines,
        prescription: rxLines,
      });
      await api.post(`/cases/${id}/action`, { action: "close", note: "Gestito direttamente" });
      toast.success("Caso gestito e chiuso");
      navigate("/casi");
    } catch { toast.error("Errore nella chiusura del caso"); }
  };

  const saveExamsAndClose = async () => {
    const id = caseId || await createOrUpdateCase();
    if (!id) return;
    if (examLines.length === 0) { toast.error("Aggiungi almeno un esame"); return; }
    try {
      await api.put(`/cases/${id}`, { exam_requests: examLines });
      await api.post(`/cases/${id}/action`, { action: "close", note: "Esami richiesti" });
      toast.success("Esami salvati, caso chiuso");
      navigate("/casi");
    } catch { toast.error("Errore"); }
  };

  // Salva la richiesta esami MA NON chiude il caso — usato quando il MMG
  // vuole proseguire con "gestione diretta" o "contatta specialista".
  const saveExamsAndKeepOpen = async () => {
    const id = caseId || await createOrUpdateCase();
    if (!id) return false;
    if (examLines.length === 0) { toast.error("Aggiungi almeno un esame"); return false; }
    try {
      await api.put(`/cases/${id}`, { exam_requests: examLines });
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio degli esami");
      return false;
    }
  };

  const suggestExamsWithAI = async () => {
    const id = caseId || await createOrUpdateCase();
    if (!id) return;
    setAiBusy(true); setAiSuggestions([]); setExamsRationale("");
    try {
      const hyps = (selectedHyps || []).map((h) => h?.diagnosis).filter(Boolean);
      const already = (examLines || []).map((e) => e?.text).filter(Boolean);
      const { data } = await api.post(`/cases/${id}/ai-suggest-exams`,
        { max_items: 8, hypotheses: hyps, already_done: already });
      setAiSuggestions(data.suggestions || []);
      // Costruiamo un razionale complessivo unendo le priorità 'urgente'
      if (data.suggestions?.length) {
        const urgent = data.suggestions.filter(s => s.priority === "urgente").length;
        const diffMsg = hyps.length > 1
          ? ` Esami scelti per **differenziare** tra: ${hyps.join(" · ")}.`
          : "";
        setExamsRationale(
          (urgent > 0
            ? `Il quadro clinico richiede accertamenti immediati (${urgent} esame/i urgenti su ${data.suggestions.length}).`
            : `Esami di completamento diagnostico proposti in base al quadro clinico raccolto.`) + diffMsg
        );
      }
      if (!data.suggestions?.length) toast.info("L'AI non ha proposto esami");
      else toast.success(`${data.suggestions.length} esami suggeriti dall'AI`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI");
    } finally { setAiBusy(false); }
  };

  const runDifferentialDx = async () => {
    const id = caseId || await createOrUpdateCase();
    if (!id) return;
    setDxBusy(true); setDxItems([]);
    try {
      const { data } = await api.post(`/cases/${id}/ai-differential-diagnosis`, { max_items: 3 });
      setDxItems(data.differentials || []);
      if (!data.differentials?.length) toast.info("L'AI non ha prodotto ipotesi");
      else toast.success(`${data.differentials.length} ipotesi diagnostiche pronte`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI");
    } finally { setDxBusy(false); }
  };

  const runAiHint = async () => {
    if (!reasonFree.trim()) { toast.error("Scrivi qualcosa nel campo libero"); return; }
    const id = caseId || await createOrUpdateCase();
    if (!id) return;
    setHintBusy(true); setHint(null);
    try {
      const { data } = await api.post(`/cases/${id}/ai-hint-from-text`, { text: reasonFree.trim() });
      setHint(data.hint || null);
      toast.success("Analisi AI pronta");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI");
    } finally { setHintBusy(false); }
  };

  const applyHintModule = (id) => {
    if (!id || !modulesList.some(m => m.id === id)) return;
    if (!reasonCodes.includes("sintomo")) setReasonCodes([...reasonCodes, "sintomo"]);
    setSymptomModule(id);
    loadModule(id);
    toast.success("Modulo selezionato dall'AI");
  };

  const runAiPrescription = async () => {
    const id = caseId || await createOrUpdateCase();
    if (!id) return;
    setRxAiBusy(true); setRxAiRationale(""); setRxAiSuggestions([]);
    try {
      const { data } = await api.post(`/cases/${id}/ai-suggest-prescription`, { max_items: 6 });
      setRxAiRationale(data.rationale || "");
      setRxAiSuggestions(data.suggestions || []);
      if (!data.suggestions?.length) toast.info("L'AI non propone terapia farmacologica");
      else toast.success(`${data.suggestions.length} farmaci suggeriti dall'AI`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI");
    } finally { setRxAiBusy(false); }
  };

  // AI: propone indicazioni NON farmacologiche (stile di vita, dieta, esercizio,
  // igiene del sonno, presidi, educazione al paziente) e le mette nel campo.
  const suggestNonPharmaWithAI = async () => {
    const id = caseId || await createOrUpdateCase();
    if (!id) return;
    setNonPharmaBusy(true);
    try {
      const hyps = (selectedHyps || []).map((h) => h?.diagnosis).filter(Boolean);
      const { data } = await api.post(`/cases/${id}/ai-suggest-non-pharma`, {
        hypotheses: hyps,
      });
      const text = (data.text || "").trim();
      if (!text) { toast.info("L'AI non ha proposto indicazioni"); return; }
      setMgmt((s) => ({
        ...s,
        indicazioni_non_farmacologiche: s.indicazioni_non_farmacologiche
          ? s.indicazioni_non_farmacologiche + "\n" + text
          : text,
      }));
      toast.success("Indicazioni non farmacologiche generate dall'AI");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI");
    } finally { setNonPharmaBusy(false); }
  };

  const discussOwnHypothesis = async () => {
    if (!otherHypText.trim()) { toast.error("Scrivi la tua ipotesi"); return; }
    const id = caseId || await createOrUpdateCase();
    if (!id) return;
    setOtherHypBusy(true); setOtherHypResp(null);
    try {
      const { data } = await api.post(`/cases/${id}/ai-discuss-hypothesis`, {
        hypothesis: otherHypText.trim(), reasoning: otherHypReasoning.trim(),
      });
      setOtherHypResp(data.response || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI");
    } finally { setOtherHypBusy(false); }
  };

  const chooseHypothesis = async (hyp) => {
    // Toggle: aggiunge o rimuove l'ipotesi dalla selezione (max 3)
    setSelectedHyps((prev) => {
      const key = (h) => `${h?.diagnosis || ""}|${h?.source || ""}`;
      const target = key(hyp);
      if (prev.some((h) => key(h) === target)) {
        return prev.filter((h) => key(h) !== target);
      }
      if (prev.length >= 3) {
        toast.info("Massimo 3 ipotesi selezionabili");
        return prev;
      }
      return [...prev, hyp];
    });
    if (!caseId) return;
    // Salva la selezione aggiornata sul caso (async fire-and-forget)
    setTimeout(async () => {
      try {
        const nextList = (() => {
          const key = (h) => `${h?.diagnosis || ""}|${h?.source || ""}`;
          const target = key(hyp);
          if (selectedHyps.some((h) => key(h) === target)) {
            return selectedHyps.filter((h) => key(h) !== target);
          }
          if (selectedHyps.length >= 3) return selectedHyps;
          return [...selectedHyps, hyp];
        })();
        await api.put(`/cases/${caseId}`, { selected_hypotheses: nextList });
      } catch { /* silenzioso */ }
    }, 0);
  };

  const saveRxAndClose = async () => {
    const id = caseId || await createOrUpdateCase();
    if (!id) return;
    if (rxLines.length === 0) { toast.error("Aggiungi almeno un farmaco"); return; }
    try {
      await api.put(`/cases/${id}`, { prescription: rxLines });
      await api.post(`/cases/${id}/action`, { action: "close", note: "Prescrizione gestita" });
      toast.success("Prescrizione salvata, caso chiuso");
      navigate("/casi");
    } catch { toast.error("Errore"); }
  };

  // Salva la prescrizione MA NON chiude il caso — per concatenare con altri pathway
  const saveRxAndKeepOpen = async () => {
    const id = caseId || await createOrUpdateCase();
    if (!id) return false;
    if (rxLines.length === 0) { toast.error("Aggiungi almeno un farmaco"); return false; }
    try {
      await api.put(`/cases/${id}`, { prescription: rxLines });
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio della prescrizione");
      return false;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Button variant="outline" onClick={() => navigate("/dashboard")}
                  data-testid="case-back-btn" className="h-10">
            <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
          </Button>
          {caseId && (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
              Caso #{caseId.slice(0, 8)} · {caseStatus}
            </Badge>
          )}
        </div>

        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Nuova valutazione clinica</h1>
          <p className="text-sm text-slate-500">Un unico posto per raccogliere motivo, anamnesi, esame e valutazione. Le informazioni vengono riutilizzate automaticamente.</p>
        </div>

        {/* Paziente */}
        <Card className="p-4 rounded-2xl border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <User className="h-4 w-4 text-slate-700" />
            <h3 className="font-semibold text-slate-900">Paziente</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Nome *</Label>
              <Input value={pFirst} onChange={(e) => setPFirst(e.target.value)} data-testid="case-patient-first" /></div>
            <div><Label>Cognome *</Label>
              <Input value={pLast} onChange={(e) => setPLast(e.target.value)} data-testid="case-patient-last" /></div>
            <div><Label>Sesso</Label>
              <select value={pSex} onChange={(e) => setPSex(e.target.value)}
                      className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                      data-testid="case-patient-sex">
                <option value="">—</option><option value="M">Maschile</option><option value="F">Femminile</option>
              </select>
            </div>
            <div><Label>Data di nascita</Label>
              <Input type="date" value={pBirth} onChange={(e) => setPBirth(e.target.value)} data-testid="case-patient-birth" /></div>
            <div><Label>Luogo di nascita (comune)</Label>
              <Input list="case-comuni-list" value={pBirthPlace}
                     onChange={(e) => setPBirthPlace(e.target.value)}
                     placeholder="Es. Roma" data-testid="case-patient-birth-place" />
              <datalist id="case-comuni-list">
                {COMUNI_LIST.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div><Label>Provincia</Label>
              <select value={pBirthProvince} onChange={(e) => setPBirthProvince(e.target.value)}
                      data-testid="case-patient-birth-province"
                      className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="">—</option>
                {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div><Label>Età</Label>
              <Input value={pAge !== "" ? `${pAge} anni` : ""} readOnly
                     className="bg-slate-50" data-testid="case-patient-age" /></div>
            <div><Label>Telefono</Label>
              <Input value={pPhone} onChange={(e) => setPPhone(e.target.value)}
                     placeholder="Es. +39 333 1234567" data-testid="case-patient-phone" /></div>
            <div className="md:col-span-2">
              <Label className="flex items-center justify-between">
                Codice fiscale
                {!pCFManual && pCF && (
                  <span className="text-[10px] text-emerald-600 font-normal">✓ calcolato automaticamente</span>
                )}
              </Label>
              <Input value={pCF}
                     onChange={(e) => { setPCF(e.target.value.toUpperCase()); setPCFManual(true); }}
                     maxLength={16} placeholder="Es. RSSMRA80A01H501U (16 caratteri)"
                     data-testid="case-patient-cf"
                     className={pCFManual ? "" : "bg-emerald-50/40"} />
              {pCFManual && (
                <button type="button" onClick={() => { setPCFManual(false); setPCF(""); }}
                        className="text-[11px] text-slate-500 hover:text-slate-700 underline mt-0.5">
                  Ricalcola dai dati sopra
                </button>
              )}
            </div>
            <div className="md:col-span-2"><Label>Email</Label>
              <Input type="email" value={pEmail} onChange={(e) => setPEmail(e.target.value.trim())}
                     data-testid="case-patient-email" /></div>

            {/* --- Banner "Paziente già visitato" --- */}
            {!pastPatientDismissed && pastVisits.length > 0 && !routeCaseId && (
              <div className="md:col-span-2 rounded-xl border-2 border-orange-300 bg-orange-50/70 p-3"
                   data-testid="past-patient-banner">
                <div className="flex items-start gap-2 mb-2">
                  <History className="h-5 w-5 text-orange-700 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-orange-900 text-sm">
                      Paziente già visitato ({pastVisits.length} visit{pastVisits.length === 1 ? "a" : "e"} precedent{pastVisits.length === 1 ? "e" : "i"})
                    </div>
                    <div className="text-[12px] text-orange-800 mt-0.5">
                      Vuoi richiamare l'anagrafica dalla visita precedente? L'anamnesi resta da compilare o rivedere.
                    </div>
                  </div>
                  <button onClick={() => setPastPatientDismissed(true)}
                          className="text-orange-500 hover:text-orange-800"
                          data-testid="past-patient-dismiss">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                {pastVisitsSummary && (
                  <div className="rounded-md bg-white border border-orange-200 p-2 mb-2 text-[12px] text-slate-700 italic">
                    <span className="font-semibold text-orange-800">Riassunto ultima visita:</span> {pastVisitsSummary}
                  </div>
                )}
                <ul className="space-y-1 mb-2 max-h-40 overflow-y-auto">
                  {pastVisits.slice(0, 8).map((v) => (
                    <li key={v.id}
                        className={`rounded-md border p-2 flex items-center gap-2 text-[12px] cursor-pointer transition
                          ${linkedPreviousCaseId === v.id
                             ? "border-orange-500 bg-orange-100"
                             : "border-orange-200 bg-white hover:border-orange-400"}`}
                        onClick={() => setLinkedPreviousCaseId(linkedPreviousCaseId === v.id ? null : v.id)}
                        data-testid={`past-visit-${v.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900">
                          {v.created_at ? new Date(v.created_at).toLocaleDateString('it-IT') : "—"} · {v.symptom_module || (v.reason_codes || []).join(", ") || "visita generica"}
                        </div>
                        {v.clinical_evaluation && (
                          <div className="text-[11px] text-slate-600 truncate">{v.clinical_evaluation}</div>
                        )}
                      </div>
                      {linkedPreviousCaseId === v.id && (
                        <Check className="h-4 w-4 text-orange-700 flex-shrink-0" />
                      )}
                      <button onClick={(e) => { e.stopPropagation(); window.open(`/casi/${v.id}`, "_blank"); }}
                              className="text-orange-700 hover:text-orange-900 text-[10px] font-semibold underline"
                              data-testid={`past-visit-open-${v.id}`}>
                        Apri
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={recallAnagrafica}
                          data-testid="past-patient-recall"
                          className="bg-orange-600 hover:bg-orange-700 text-white h-8 text-[11px]">
                    <User className="h-3.5 w-3.5 mr-1" /> Richiama anagrafica
                  </Button>
                  {linkedPreviousCaseId && (
                    <span className="text-[11px] text-orange-900 self-center italic">
                      Il nuovo caso sarà collegato alla visita del {new Date(pastVisits.find(v => v.id === linkedPreviousCaseId)?.created_at || Date.now()).toLocaleDateString('it-IT')}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Motivo visita */}
        <Card className="p-4 rounded-2xl border-slate-200 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-1">Perché il paziente è venuto oggi?</h3>
          <p className="text-xs text-slate-500 mb-3">Seleziona uno o più motivi. Cliccando su un motivo si aprirà sotto un pannello con "Il paziente riferisce" e — per esami o prescrizione — la lista pronta per la segretaria.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {REASONS.map((r) => {
              const Icon = r.icon;
              const active = reasonCodes.includes(r.code);
              return (
                <button key={r.code} type="button" onClick={() => toggleReason(r.code)}
                        data-testid={`case-reason-${r.code}`}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition
                          ${active ? `${r.color} ring-2 ring-offset-1 ring-slate-300` : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"}`}>
                  <Icon className="h-4 w-4" />
                  <span className="flex-1 text-left">{r.label}</span>
                  {active && <Check className="h-4 w-4" />}
                </button>
              );
            })}
          </div>
        </Card>

        {/* Pannello che si apre DOPO aver cliccato un motivo — Il paziente riferisce + AI+PDF per esami/farmaci */}
        {reasonCodes.length > 0 && (
          <Card className="p-4 rounded-2xl border-blue-200 bg-blue-50/30 shadow-sm" data-testid="case-reason-panel">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
              <Label className="text-[13px] font-semibold text-slate-800">Il paziente riferisce</Label>
              <Button size="sm" variant="outline" onClick={runAiHint}
                      disabled={hintBusy || !reasonFree.trim()}
                      data-testid="case-ai-hint-btn"
                      className="border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50 h-7 text-[11px]">
                <Wand2 className="h-3 w-3 mr-1" />
                {hintBusy ? "Analisi…" : "AI: aiutami a orientarmi"}
              </Button>
            </div>
            <Textarea rows={3} value={reasonFree} onChange={(e) => setReasonFree(e.target.value)}
                      placeholder={reasonCodes.includes("farmaci")
                        ? "Es. rinnovo della terapia antipertensiva in corso; il paziente riferisce buona tolleranza…"
                        : reasonCodes.includes("esami")
                        ? "Es. richiesta esami di controllo semestrali; funzionalità tiroidea da valutare…"
                        : "Es. paziente riferisce dolore al petto da 30 min con sudorazione…"}
                      data-testid="case-reason-free" />

            {hint && (
              <div className="mt-2 rounded-xl bg-fuchsia-50 border border-fuchsia-200 p-2.5"
                   data-testid="case-ai-hint-box">
                <div className="flex items-start gap-2">
                  <Lightbulb className="h-4 w-4 text-fuchsia-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    {hint.suggested_module_id && (
                      <div className="mb-1.5">
                        <div className="text-[11px] text-fuchsia-800 mb-1">
                          Modulo consigliato ({hint.confidence || 0}% confidenza):
                        </div>
                        <Button size="sm"
                                onClick={() => applyHintModule(hint.suggested_module_id)}
                                data-testid="case-ai-hint-apply-module"
                                className="h-7 text-[11px] bg-fuchsia-600 hover:bg-fuchsia-700 text-white">
                          <Check className="h-3 w-3 mr-1" /> Usa "{hint.suggested_module_label}"
                        </Button>
                      </div>
                    )}
                    {hint.initial_red_flags?.length > 0 && (
                      <div className="mt-1">
                        <div className="text-[11px] font-semibold text-red-700 mb-0.5">Red flag da attenzionare:</div>
                        <ul className="text-[12px] text-slate-800 list-disc pl-5 space-y-0.5">
                          {hint.initial_red_flags.map((f, i) => (<li key={i}>{f}</li>))}
                        </ul>
                      </div>
                    )}
                    {hint.first_moves && (
                      <div className="mt-1.5 text-[12px] text-slate-700">
                        <span className="font-semibold">Prime azioni: </span>{hint.first_moves}
                      </div>
                    )}
                    <div className="mt-1 flex justify-end">
                      <button onClick={() => setHint(null)}
                              className="text-[11px] text-slate-500 hover:text-slate-800 underline">
                        Nascondi
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Sub-pannello esami rapido */}
            {reasonCodes.includes("esami") && (
              <QuickListPanel
                type="exams"
                lines={examLines}
                setLines={setExamLines}
                aiSuggestions={aiSuggestions}
                setAiSuggestions={setAiSuggestions}
                onAiSuggest={suggestExamsWithAI}
                aiBusy={aiBusy}
                caseId={caseId}
                canCreate={canCreateCase}
                createOrUpdateCase={createOrUpdateCase}
                examsRationale={examsRationale}
              />
            )}

            {/* Sub-pannello prescrizione rapido */}
            {reasonCodes.includes("farmaci") && (
              <QuickListPanel
                type="prescription"
                lines={rxLines}
                setLines={setRxLines}
                aiSuggestions={rxAiSuggestions}
                setAiSuggestions={setRxAiSuggestions}
                aiRationale={rxAiRationale}
                onAiSuggest={runAiPrescription}
                aiBusy={rxAiBusy}
                caseId={caseId}
                canCreate={canCreateCase}
                createOrUpdateCase={createOrUpdateCase}
              />
            )}
          </Card>
        )}

        {/* Modulo Sintomo — appare solo se selezionato "sintomo" */}
        {includeSymptom && (
          <Card className="p-4 rounded-2xl border-rose-200 shadow-sm bg-rose-50/30">
            <h3 className="font-semibold text-slate-900 mb-2">Modulo sintomo per specializzazione</h3>
            <ModulesBySpecialization modules={modulesList}
                                      selected={symptomModule}
                                      onPick={(id) => {
                                        if (id === "__altro") {
                                          setSymptomModule("");
                                          setModuleData(null);
                                          toast.info("Nessun modulo — usa il testo libero e il modulo generico");
                                        } else {
                                          setSymptomModule(id);
                                          loadModule(id);
                                        }
                                      }} />

            {/* Red-flag banner in tempo reale */}
            {redFlags.length > 0 && (
              <div data-testid="case-red-flags"
                   className={`mb-3 rounded-xl border-2 p-3
                     ${redFlags.some(f => f.severity === "critical")
                       ? "bg-red-50 border-red-400"
                       : "bg-amber-50 border-amber-400"}`}>
                <div className="flex items-start gap-2">
                  <AlertTriangle className={`h-5 w-5 mt-0.5 flex-shrink-0
                    ${redFlags.some(f => f.severity === "critical") ? "text-red-600" : "text-amber-600"}`} />
                  <div className="flex-1">
                    <div className="font-bold text-red-800 mb-1">
                      Alert clinico{redFlags.length > 1 ? ` — ${redFlags.length} avvisi` : ""}
                    </div>
                    <ul className="space-y-0.5 text-sm text-slate-800 list-disc pl-5">
                      {redFlags.map((f, i) => (
                        <li key={i} className={f.severity === "critical" ? "font-semibold text-red-700" : "text-amber-700"}>
                          {f.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {moduleData && (
              <div className="space-y-3" data-testid="case-symptom-questions">
                {moduleData.questions.map((q, i) => {
                  const a = symptomAnswers[q.id] || {};
                  return (
                    <div key={q.id} className={`rounded-lg border p-3 ${a.skipped ? "bg-slate-100 border-slate-200 opacity-60" : "bg-white border-slate-200"}`}
                         data-testid={`case-q-${q.id}`}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <Label className="text-[13px] font-semibold">{i + 1}. {q.label}</Label>
                        <button type="button" onClick={() =>
                            setSymptomAnswers(s => ({ ...s, [q.id]: { ...(s[q.id]||{}), skipped: !a.skipped } }))}
                                data-testid={`case-q-skip-${q.id}`}
                                className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${a.skipped ? "bg-slate-500 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                          {a.skipped ? "Saltata" : "Salta"}
                        </button>
                      </div>
                      {q.hint && <p className="text-[11px] text-slate-500 mb-1.5">{q.hint}</p>}
                      {q.options ? (
                        <div className="flex gap-1.5 flex-wrap">
                          {q.options.map((opt) => (
                            <button key={opt} type="button"
                                    onClick={() => setSymptomAnswers(s => ({ ...s, [q.id]: { ...(s[q.id]||{}), answer: opt, skipped: false } }))}
                                    className={`text-[12px] px-2 py-1 rounded border ${a.answer === opt ? "bg-rose-100 border-rose-400 text-rose-800" : "bg-white border-slate-200 text-slate-700 hover:border-rose-300"}`}>
                              {opt}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <Input value={a.answer || ""}
                               onChange={(e) => setSymptomAnswers(s => ({ ...s, [q.id]: { ...(s[q.id]||{}), answer: e.target.value, skipped: false } }))}
                               placeholder="Risposta (opzionale)"
                               data-testid={`case-q-answer-${q.id}`} className="h-9 text-sm" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        )}

        {/* Anamnesi rilevante */}
        <Card className="p-4 rounded-2xl border-slate-200 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-2">Anamnesi rilevante</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              ["patologie","Patologie"],["allergie","Allergie"],
              ["farmaci","Farmaci in uso"],["interventi","Interventi"],
            ].map(([k, lbl]) => (
              <div key={k}>
                <Label className="text-[12px]">{lbl}</Label>
                <Textarea rows={2} value={an[k]}
                          onChange={(e) => setAn(s => ({ ...s, [k]: e.target.value }))}
                          data-testid={`case-an-${k}`} />
              </div>
            ))}
            <div className="md:col-span-2">
              <Label className="text-[12px]">Altre informazioni rilevanti</Label>
              <Textarea rows={2} value={an.altre}
                        onChange={(e) => setAn(s => ({ ...s, altre: e.target.value }))}
                        data-testid="case-an-altre" />
            </div>
          </div>
        </Card>

        {/* Esame obiettivo + Valutazione */}
        <Card className="p-4 rounded-2xl border-slate-200 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="font-semibold">Esame obiettivo</Label>
              <Textarea rows={4} value={examination}
                        onChange={(e) => setExamination(e.target.value)}
                        placeholder="Testo libero…"
                        data-testid="case-examination" />
            </div>
            <div>
              <Label className="font-semibold">Valutazione clinica</Label>
              <Textarea rows={4} value={evaluation}
                        onChange={(e) => setEvaluation(e.target.value)}
                        placeholder="Testo libero…"
                        data-testid="case-evaluation" />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between gap-2 flex-wrap border-t border-slate-100 pt-3">
            <div className="text-[12px] text-slate-500">
              Puoi chiedere all'AI di proporre 3 ipotesi diagnostiche differenziali basate sul caso.
              <span className="italic"> Le ipotesi vanno sempre validate dal medico.</span>
            </div>
            <Button size="sm" variant="outline" onClick={runDifferentialDx}
                    disabled={dxBusy || !canCreateCase}
                    data-testid="case-dx-run"
                    className="border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50">
              <Brain className="h-3.5 w-3.5 mr-1.5" />
              {dxBusy ? "AI in analisi…" : "Diagnosi differenziale (AI)"}
            </Button>
          </div>
          {dxItems.length > 0 && (
            <DifferentialDxBox items={dxItems} onDismiss={() => setDxItems([])}
                               selectedHyps={selectedHyps}
                               onSelect={chooseHypothesis}
                               onCopyToEvaluation={(txt) =>
                                 setEvaluation(prev => (prev ? prev + "\n\n" + txt : txt))} />
          )}
          {/* "Altro" — confronto MMG vs AI */}
          {dxItems.length > 0 && (
            <div className="mt-3 rounded-xl border-2 border-dashed border-slate-300 bg-white p-3"
                 data-testid="case-other-hyp">
              <div className="text-[12px] font-semibold text-slate-800 mb-1.5 flex items-center gap-1.5">
                <Brain className="h-3.5 w-3.5" /> Nessuna di queste? Scrivi la tua ipotesi
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Input value={otherHypText} onChange={(e) => setOtherHypText(e.target.value)}
                       placeholder="Es. Pericardite acuta"
                       data-testid="case-other-hyp-text" />
                <Input value={otherHypReasoning} onChange={(e) => setOtherHypReasoning(e.target.value)}
                       placeholder="(opzionale) come ci sei arrivato"
                       data-testid="case-other-hyp-reason" />
              </div>
              <div className="flex justify-end mt-2">
                <Button size="sm" onClick={discussOwnHypothesis} disabled={otherHypBusy || !otherHypText.trim()}
                        data-testid="case-other-hyp-submit"
                        className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white h-8 text-[12px]">
                  {otherHypBusy ? "AI in analisi…" : "Chiedi il parere all'AI"}
                </Button>
              </div>
              {otherHypResp && (
                <div className="mt-2 rounded-lg border-2 p-3"
                     data-testid="case-other-hyp-response"
                     style={{ borderColor: otherHypResp.agreement === "agree" ? "#10b981" : otherHypResp.agreement === "disagree" ? "#ef4444" : "#f59e0b" }}>
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <Badge className={
                      otherHypResp.agreement === "agree" ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                      : otherHypResp.agreement === "disagree" ? "bg-red-100 text-red-800 border-red-300"
                      : "bg-amber-100 text-amber-800 border-amber-300"}>
                      {otherHypResp.agreement === "agree" ? "L'AI concorda"
                       : otherHypResp.agreement === "disagree" ? "L'AI dissente" : "L'AI concorda parzialmente"}
                    </Badge>
                    <span className="text-[12px] text-slate-600">
                      Probabilità stimata: <b>{otherHypResp.probability}%</b>
                    </span>
                  </div>
                  <div className="text-[13px] text-slate-800 whitespace-pre-wrap">{otherHypResp.comment}</div>
                  {otherHypResp.alternative_hypotheses?.length > 0 && (
                    <div className="mt-2 text-[12px]">
                      <span className="font-semibold text-slate-700">Alternative da considerare: </span>
                      {otherHypResp.alternative_hypotheses.join(" · ")}
                    </div>
                  )}
                  {otherHypResp.next_verifications && (
                    <div className="mt-1 text-[12px] text-slate-700">
                      <span className="font-semibold">Verifiche consigliate: </span>{otherHypResp.next_verifications}
                    </div>
                  )}
                  <div className="mt-2 flex justify-end">
                    <Button size="sm" variant="outline"
                            onClick={() => chooseHypothesis({
                              diagnosis: otherHypText.trim(),
                              source: "mmg",
                              probability: otherHypResp.probability,
                              ai_agreement: otherHypResp.agreement,
                              rationale: otherHypResp.comment,
                            })}
                            data-testid="case-other-hyp-choose"
                            className="h-7 text-[11px] border-fuchsia-300 text-fuchsia-700">
                      <Check className="h-3 w-3 mr-1" /> Usa la mia ipotesi
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Bottone salva-continua */}
        {!pathway && (
          <div className="flex justify-end">
            <Button onClick={createOrUpdateCase} disabled={!canCreateCase || savingCase}
                    data-testid="case-save-btn"
                    className="h-11 bg-gradient-to-r from-slate-700 to-slate-900 text-white">
              <Save className="h-4 w-4 mr-2" /> {caseId ? "Salva caso" : "Crea caso"}
            </Button>
          </div>
        )}

        {/* Come vuoi procedere? */}
        {(caseId || canCreateCase) && !pathway && (
          <Card className="p-4 rounded-2xl border-blue-200 bg-blue-50/40 shadow-sm">
            <h3 className="font-heading text-lg font-bold text-slate-900 mb-1">Come vuoi procedere?</h3>
            <p className="text-xs text-slate-600 mb-3">Le informazioni già inserite verranno riutilizzate automaticamente.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {PATHWAYS.map((p) => {
                const Icon = p.icon;
                return (
                  <button key={p.code} type="button" onClick={() => choosePathway(p.code)}
                          data-testid={`case-pathway-${p.code}`}
                          className="text-left flex items-start gap-3 rounded-xl border-2 border-slate-200 bg-white p-3 hover:border-blue-400 hover:shadow-md transition">
                    <span className={`mt-1 h-3 w-3 rounded-full ${p.dotCls} ring-2 ring-white flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <Icon className="h-4 w-4 text-slate-700" />
                        <span className="font-semibold text-slate-900 text-sm">{p.label}</span>
                      </div>
                      <p className="text-[11px] text-slate-600 mt-1">{p.desc}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-300 mt-2 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          </Card>
        )}

        {/* Percorso: gestione diretta */}
        {pathway === "manage_direct" && (
          <PathwayCard title="Gestione diretta del caso" onBack={() => setPathway(null)}>
            <ManagementBlock
              mgmt={mgmt} setMgmt={setMgmt}
              examLines={examLines} setExamLines={setExamLines}
              rxLines={rxLines} setRxLines={setRxLines}
              selectedHyps={selectedHyps}
              aiBusy={aiBusy} aiSuggestions={aiSuggestions} setAiSuggestions={setAiSuggestions}
              examsRationale={examsRationale} suggestExams={suggestExamsWithAI}
              rxAiBusy={rxAiBusy} rxAiRationale={rxAiRationale}
              rxAiSuggestions={rxAiSuggestions} setRxAiSuggestions={setRxAiSuggestions}
              suggestRx={runAiPrescription}
              nonPharmaBusy={nonPharmaBusy} suggestNonPharma={suggestNonPharmaWithAI}
            />
            <div className="flex justify-end mt-3">
              <Button onClick={saveManagementAndClose}
                      data-testid="case-mgmt-close"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Check className="h-4 w-4 mr-2" /> Salva tutto e chiudi il caso
              </Button>
            </div>
            <div className="text-[10px] italic text-slate-500 mt-1 text-right">
              Tutto il piano di gestione (esami, terapia, indicazioni non farmacologiche, follow-up) sarà rielaborato dall'AI in un referto narrativo.
            </div>
          </PathwayCard>
        )}

        {/* Percorso: crea Sinapsi — invio reale */}
        {pathway === "create_sinapsi" && (
          <PathwayCard title="Invio Sinapsi allo specialista" onBack={() => setPathway(null)}>
            <p className="text-[12px] text-slate-600 mb-2">
              La bozza è stata <strong>generata automaticamente</strong> dai dati del caso.
              Scegli lo specialista, modifica il testo se serve e invia.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="text-[12px]">Specializzazione richiesta</Label>
                <Input value={sinapsiSpec}
                       onChange={(e) => setSinapsiSpec(e.target.value)}
                       onBlur={() => loadSpecialistsForSpec(sinapsiSpec)}
                       placeholder="Es. Otorinolaringoiatria"
                       data-testid="case-sinapsi-spec" />
                <SpecSuggestHint testid="case-sinapsi-spec-hint" />
              </div>
              <div>
                <Label className="text-[12px]">Note aggiuntive (opzionale)</Label>
                <Input value={sinapsiAdd} onChange={(e) => setSinapsiAdd(e.target.value)}
                       placeholder="Richiesta specifica del consulto…"
                       data-testid="case-sinapsi-add" />
              </div>
            </div>

            {/* Elenco specialisti disponibili */}
            <div className="mb-3">
              <Label className="text-[12px]">Specialista destinatario *</Label>
              <div className="flex items-center gap-2 mb-2">
                <Button size="sm" variant="outline"
                        onClick={() => loadSpecialistsForSpec(sinapsiSpec)}
                        data-testid="case-load-specialists">
                  Cerca specialisti
                </Button>
                <span className="text-[11px] text-slate-500">
                  {specialists.length > 0 ? `${specialists.length} colleghi trovati` : "nessun risultato"}
                </span>
              </div>
              {specialists.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-56 overflow-auto pr-1"
                     data-testid="case-specialists-list">
                  {specialists.map((s) => (
                    <button key={s.id} type="button"
                            onClick={() => setSelectedSpecialist(s)}
                            data-testid={`case-specialist-${s.id}`}
                            className={`text-left rounded-lg border-2 p-2.5 transition
                              ${selectedSpecialist?.id === s.id
                                ? "border-blue-500 bg-blue-50"
                                : "border-slate-200 bg-white hover:border-blue-300"}`}>
                      <div className="text-sm font-semibold text-slate-900">
                        {s.first_name} {s.last_name}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {s.specialization || s.profession} · {s.email}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
              <div>
                <Label className="text-[12px]">Priorità</Label>
                <select className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                        value={sinapsiPriority} onChange={(e) => setSinapsiPriority(e.target.value)}
                        data-testid="case-sinapsi-priority">
                  <option value="bassa">Bassa</option>
                  <option value="normale">Normale</option>
                  <option value="alta">Alta</option>
                  <option value="critica">Critica</option>
                </select>
              </div>
              <div>
                <Label className="text-[12px]">Urgenza</Label>
                <select className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                        value={sinapsiUrgency} onChange={(e) => setSinapsiUrgency(e.target.value)}
                        data-testid="case-sinapsi-urgency">
                  <option value="normale">Normale</option>
                  <option value="alto">Alto</option>
                  <option value="critico">Critico</option>
                </select>
              </div>
            </div>

            <div className="mb-2 flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => generateSinapsiDraft()}
                      disabled={sinapsiBusy} data-testid="case-sinapsi-refresh">
                <FileText className="h-3.5 w-3.5 mr-1.5" /> Rigenera bozza dai dati del caso
              </Button>
            </div>
            <Textarea rows={12} value={sinapsiDraft}
                      onChange={(e) => setSinapsiDraft(e.target.value)}
                      className="font-mono text-[12px]"
                      data-testid="case-sinapsi-draft" />

            <div className="flex justify-end mt-3 gap-2">
              <Button variant="outline" onClick={async () => {
                  const id = caseId || await createOrUpdateCase();
                  if (id) navigate("/casi");
              }} data-testid="case-sinapsi-save">
                <Save className="h-4 w-4 mr-2" /> Salva bozza nel caso
              </Button>
              <Button onClick={sendSinapsi} disabled={sending || !selectedSpecialist}
                      data-testid="case-sinapsi-send"
                      className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white">
                <Send className="h-4 w-4 mr-2" />
                {sending ? "Invio…" : "Invia Sinapsi al collega"}
              </Button>
            </div>
          </PathwayCard>
        )}

        {/* Percorso: esami */}
        {pathway === "request_exams" && (
          <PathwayCard title="Richiesta esami/accertamenti" onBack={() => setPathway(null)}>
            <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
              <p className="text-[12px] text-slate-600">
                Compila gli esami manualmente oppure lascia che l'AI proponga un elenco ragionato basato sul caso.
              </p>
              <Button size="sm" variant="outline" onClick={suggestExamsWithAI}
                      disabled={aiBusy}
                      data-testid="case-exams-ai-suggest"
                      className="border-purple-300 text-purple-700 hover:bg-purple-50">
                <FileText className="h-3.5 w-3.5 mr-1.5" />
                {aiBusy ? "AI in analisi…" : "Suggerisci con AI"}
              </Button>
            </div>

            {aiSuggestions.length > 0 && (
              <div className="mb-3 rounded-xl border-2 border-purple-200 bg-purple-50/50 p-3"
                   data-testid="case-ai-suggestions">
                {examsRationale && (
                  <div className="mb-3 rounded-lg bg-white border border-purple-200 p-2.5"
                       data-testid="case-ai-exams-rationale">
                    <div className="flex items-start gap-2">
                      <Sparkles className="h-4 w-4 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-[11px] font-bold text-purple-800 mb-0.5">Perché l'AI propone questi esami</div>
                        <div className="text-[12px] text-slate-700">{examsRationale}</div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[12px] font-semibold text-purple-800">
                    Proposte AI ({aiSuggestions.length}) — clicca per aggiungerle
                  </div>
                  <Button size="sm" variant="ghost"
                          onClick={() => {
                            const merged = [...examLines,
                              ...aiSuggestions.map(s => ({ text: s.name, rationale: s.rationale, priority: s.priority }))];
                            setExamLines(merged); setAiSuggestions([]);
                            toast.success("Tutti i suggerimenti aggiunti");
                          }}
                          data-testid="case-ai-add-all"
                          className="text-purple-700 hover:bg-purple-100 h-7 text-[11px]">
                    Aggiungi tutti
                  </Button>
                </div>
                <ul className="space-y-1.5">
                  {aiSuggestions.map((s, i) => (
                    <li key={i}
                        className="rounded-lg bg-white border border-purple-200 p-2 flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 text-sm">{s.name}</span>
                          {s.priority === "urgente" && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">URGENTE</span>
                          )}
                        </div>
                        {s.rationale && (
                          <div className="text-[11px] text-slate-600 mt-0.5">{s.rationale}</div>
                        )}
                      </div>
                      <Button size="sm" variant="outline"
                              onClick={() => {
                                setExamLines([...examLines, { text: s.name, rationale: s.rationale, priority: s.priority }]);
                                setAiSuggestions(aiSuggestions.filter((_, j) => j !== i));
                              }}
                              data-testid={`case-ai-add-${i}`}
                              className="h-7 text-[11px] border-purple-300 text-purple-700">
                        <ListPlus className="h-3 w-3 mr-1" /> Aggiungi
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <SimpleList lines={examLines} setLines={setExamLines}
                        placeholder="Es. Emocromo completo · ECG · Ecografia tiroidea…"
                        testid="case-exams" />
            <div className="mt-3 rounded-xl border-2 border-blue-200 bg-blue-50/50 p-2.5"
                 data-testid="case-exams-chain">
              <div className="text-[11px] font-semibold text-slate-700 mb-1.5">
                Salva la richiesta esami e prosegui:
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Button onClick={saveExamsAndClose}
                        disabled={examLines.length === 0}
                        data-testid="case-exams-save-close"
                        className="bg-amber-600 hover:bg-amber-700 text-white text-[12px]">
                  <Save className="h-3.5 w-3.5 mr-1.5" /> Salva e chiudi caso
                </Button>
                <Button onClick={async () => {
                          const ok = await saveExamsAndKeepOpen();
                          if (ok) { setPathway("manage_direct"); toast.success("Passa a gestione diretta"); }
                        }}
                        disabled={examLines.length === 0}
                        data-testid="case-exams-then-manage"
                        variant="outline"
                        className="border-emerald-400 text-emerald-800 hover:bg-emerald-50 text-[12px]">
                  <ClipboardCheck className="h-3.5 w-3.5 mr-1.5" /> …poi gestisci direttamente
                </Button>
                <Button onClick={async () => {
                          const ok = await saveExamsAndKeepOpen();
                          if (ok) { setPathway("create_sinapsi"); await generateSinapsiDraft(); toast.success("Passa a contatta specialista"); }
                        }}
                        disabled={examLines.length === 0}
                        data-testid="case-exams-then-specialist"
                        variant="outline"
                        className="border-indigo-400 text-indigo-800 hover:bg-indigo-50 text-[12px]">
                  <Stethoscope className="h-3.5 w-3.5 mr-1.5" /> …poi contatta specialista
                </Button>
              </div>
              <div className="text-[10px] italic text-slate-500 mt-1.5">
                "Salva e chiudi" chiude il caso. Le altre due opzioni salvano gli esami e aprono il percorso successivo senza chiudere.
              </div>
            </div>
          </PathwayCard>
        )}

        {/* Percorso: prescrizione */}
        {pathway === "prescription" && (
          <PathwayCard title="Gestione prescrizione" onBack={() => setPathway(null)}>
            <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
              <p className="text-[12px] text-slate-600">
                Compila la prescrizione manualmente oppure chiedi all'AI un piano terapeutico ragionato con riferimento a linee guida.
              </p>
              <Button size="sm" variant="outline" onClick={runAiPrescription}
                      disabled={rxAiBusy}
                      data-testid="case-rx-ai-suggest"
                      className="border-purple-300 text-purple-700 hover:bg-purple-50">
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                {rxAiBusy ? "AI in analisi…" : "Suggerisci con AI"}
              </Button>
            </div>
            {rxAiRationale && (
              <div className="mb-3 rounded-lg bg-purple-50 border border-purple-200 p-2.5"
                   data-testid="case-rx-ai-rationale">
                <div className="flex items-start gap-2">
                  <Sparkles className="h-4 w-4 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[11px] font-bold text-purple-800 mb-0.5">Perché l'AI propone questa terapia (riferimenti in letteratura)</div>
                    <div className="text-[12px] text-slate-700">{rxAiRationale}</div>
                  </div>
                </div>
              </div>
            )}
            {rxAiSuggestions.length > 0 && (
              <div className="mb-3 rounded-xl border-2 border-purple-200 bg-purple-50/40 p-3"
                   data-testid="case-rx-ai-suggestions">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[12px] font-semibold text-purple-800">
                    Proposte AI ({rxAiSuggestions.length}) — clicca per aggiungerle
                  </div>
                  <Button size="sm" variant="ghost"
                          onClick={() => {
                            const lines = rxAiSuggestions.map(s => ({
                              text: `${s.drug} — ${s.dose}${s.duration ? " × " + s.duration : ""}${s.indication ? " (" + s.indication + ")" : ""}`,
                              cautions: s.cautions,
                            }));
                            setRxLines([...rxLines, ...lines]);
                            setRxAiSuggestions([]);
                            toast.success("Tutti i farmaci aggiunti");
                          }}
                          data-testid="case-rx-ai-add-all"
                          className="text-purple-700 hover:bg-purple-100 h-7 text-[11px]">
                    Aggiungi tutti
                  </Button>
                </div>
                <ul className="space-y-1.5">
                  {rxAiSuggestions.map((s, i) => (
                    <li key={i} className="rounded-lg bg-white border border-purple-200 p-2 flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 text-sm">{s.drug}</div>
                        <div className="text-[11px] text-slate-600">
                          <span className="font-medium">Dose:</span> {s.dose}
                          {s.duration && <span> · <span className="font-medium">Durata:</span> {s.duration}</span>}
                        </div>
                        {s.indication && <div className="text-[11px] text-slate-600"><span className="font-medium">Indicazione:</span> {s.indication}</div>}
                        {s.cautions && <div className="text-[11px] text-amber-800 mt-0.5"><AlertTriangle className="h-3 w-3 inline mr-0.5" />{s.cautions}</div>}
                      </div>
                      <Button size="sm" variant="outline"
                              onClick={() => {
                                setRxLines([...rxLines, {
                                  text: `${s.drug} — ${s.dose}${s.duration ? " × " + s.duration : ""}${s.indication ? " (" + s.indication + ")" : ""}`,
                                  cautions: s.cautions,
                                }]);
                                setRxAiSuggestions(rxAiSuggestions.filter((_, j) => j !== i));
                              }}
                              data-testid={`case-rx-ai-add-${i}`}
                              className="h-7 text-[11px] border-purple-300 text-purple-700">
                        <ListPlus className="h-3 w-3 mr-1" /> Aggiungi
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <SimpleList lines={rxLines} setLines={setRxLines}
                        placeholder="Es. Ramipril 5 mg 1 cp ore 8 x 30 gg · Paracetamolo 1000 mg al bisogno…"
                        testid="case-rx" />
            <div className="mt-3 rounded-xl border-2 border-purple-200 bg-purple-50/50 p-2.5"
                 data-testid="case-rx-chain">
              <div className="text-[11px] font-semibold text-slate-700 mb-1.5">
                Salva la prescrizione e prosegui:
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Button onClick={saveRxAndClose}
                        disabled={rxLines.length === 0}
                        data-testid="case-rx-save-close"
                        className="bg-purple-600 hover:bg-purple-700 text-white text-[12px]">
                  <Save className="h-3.5 w-3.5 mr-1.5" /> Salva e chiudi caso
                </Button>
                <Button onClick={async () => {
                          const ok = await saveRxAndKeepOpen();
                          if (ok) { setPathway("request_exams"); toast.success("Passa a richiesta esami"); }
                        }}
                        disabled={rxLines.length === 0}
                        data-testid="case-rx-then-exams"
                        variant="outline"
                        className="border-amber-400 text-amber-800 hover:bg-amber-50 text-[12px]">
                  <FileText className="h-3.5 w-3.5 mr-1.5" /> …poi richiedi esami
                </Button>
                <Button onClick={async () => {
                          const ok = await saveRxAndKeepOpen();
                          if (ok) { setPathway("create_sinapsi"); await generateSinapsiDraft(); toast.success("Passa a contatta specialista"); }
                        }}
                        disabled={rxLines.length === 0}
                        data-testid="case-rx-then-specialist"
                        variant="outline"
                        className="border-indigo-400 text-indigo-800 hover:bg-indigo-50 text-[12px]">
                  <Stethoscope className="h-3.5 w-3.5 mr-1.5" /> …poi contatta specialista
                </Button>
              </div>
              <div className="text-[10px] italic text-slate-500 mt-1.5">
                "Salva e chiudi" chiude il caso. Le altre due opzioni salvano la prescrizione e aprono il percorso successivo senza chiudere.
              </div>
            </div>
          </PathwayCard>
        )}

        {/* Percorso ibrido: gestisci + sinapsi */}
        {pathway === "manage_and_sinapsi" && (
          <PathwayCard title="Gestisci direttamente E contatta specialista" onBack={() => setPathway(null)}>
            <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-2.5 mb-3 text-[12px] text-indigo-800">
              Doppio percorso: compila esami, terapia e indicazioni al paziente e, in parallelo, invia la Sinapsi al collega.
              Il caso rimane in stato <em>"in attesa di risposta"</em> finché lo specialista non risponde.
            </div>
            <ManagementBlock
              mgmt={mgmt} setMgmt={setMgmt}
              examLines={examLines} setExamLines={setExamLines}
              rxLines={rxLines} setRxLines={setRxLines}
              selectedHyps={selectedHyps}
              aiBusy={aiBusy} aiSuggestions={aiSuggestions} setAiSuggestions={setAiSuggestions}
              examsRationale={examsRationale} suggestExams={suggestExamsWithAI}
              rxAiBusy={rxAiBusy} rxAiRationale={rxAiRationale}
              rxAiSuggestions={rxAiSuggestions} setRxAiSuggestions={setRxAiSuggestions}
              suggestRx={runAiPrescription}
              nonPharmaBusy={nonPharmaBusy} suggestNonPharma={suggestNonPharmaWithAI}
              testidPrefix="hybrid"
            />
            <div className="rounded-xl border-2 border-blue-200 bg-blue-50/40 p-3 mt-3">
              <div className="font-semibold text-slate-900 text-sm mb-2">Sinapsi allo specialista</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
                <div>
                  <Label className="text-[12px]">Specializzazione</Label>
                  <Input value={sinapsiSpec}
                         onChange={(e) => setSinapsiSpec(e.target.value)}
                         onBlur={() => loadSpecialistsForSpec(sinapsiSpec)}
                         data-testid="case-hybrid-spec" />
                  <SpecSuggestHint testid="case-hybrid-spec-hint" />
                </div>
                <div className="flex items-end">
                  <Button size="sm" variant="outline"
                          onClick={() => loadSpecialistsForSpec(sinapsiSpec)}
                          className="h-9">Cerca specialisti</Button>
                </div>
              </div>
              {specialists.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-auto mb-2">
                  {specialists.map((s) => (
                    <button key={s.id} type="button" onClick={() => setSelectedSpecialist(s)}
                            data-testid={`case-hybrid-specialist-${s.id}`}
                            className={`text-left rounded-lg border-2 p-2 transition
                              ${selectedSpecialist?.id === s.id
                                ? "border-blue-500 bg-blue-50"
                                : "border-slate-200 bg-white hover:border-blue-300"}`}>
                      <div className="text-sm font-semibold text-slate-900">{s.first_name} {s.last_name}</div>
                      <div className="text-[11px] text-slate-500">{s.specialization || s.profession}</div>
                    </button>
                  ))}
                </div>
              )}
              <Button size="sm" variant="outline" onClick={() => generateSinapsiDraft()}
                      disabled={sinapsiBusy}
                      className="h-8 text-[11px] mb-2">
                <FileText className="h-3 w-3 mr-1" /> Rigenera bozza dai dati del caso
              </Button>
              <Textarea rows={8} value={sinapsiDraft}
                        onChange={(e) => setSinapsiDraft(e.target.value)}
                        className="font-mono text-[12px]"
                        data-testid="case-hybrid-draft" />
            </div>
            <div className="flex justify-end mt-3 gap-2">
              <Button onClick={async () => {
                  const id = caseId || await createOrUpdateCase();
                  if (!id) return;
                  if (!selectedSpecialist) { toast.error("Seleziona uno specialista"); return; }
                  try {
                    await api.put(`/cases/${id}`, {
                      management: mgmt,
                      exam_requests: examLines,
                      prescription: rxLines,
                    });
                    await api.post(`/cases/${id}/send-sinapsi`, {
                      specialist_id: selectedSpecialist.id,
                      body: sinapsiDraft, priority: sinapsiPriority, urgency: sinapsiUrgency,
                    });
                    toast.success("Piano salvato + Sinapsi inviata");
                    navigate(`/casi/${id}`);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Errore");
                  }
              }}
                      data-testid="case-hybrid-submit"
                      disabled={!selectedSpecialist || !sinapsiDraft.trim()}
                      className="bg-gradient-to-r from-indigo-600 to-blue-700 text-white">
                <Send className="h-4 w-4 mr-2" /> Salva piano e invia Sinapsi
              </Button>
            </div>
          </PathwayCard>
        )}
      </div>
    </div>
  );
}

function ManagementBlock({
  mgmt, setMgmt,
  examLines, setExamLines,
  rxLines, setRxLines,
  selectedHyps,
  aiBusy, aiSuggestions, setAiSuggestions, examsRationale, suggestExams,
  rxAiBusy, rxAiRationale, rxAiSuggestions, setRxAiSuggestions, suggestRx,
  nonPharmaBusy, suggestNonPharma,
  testidPrefix = "mgmt",
}) {
  return (
    <div className="space-y-4">
      {/* ---- ESAMI / ACCERTAMENTI ---- */}
      <div className="rounded-xl border-2 border-amber-200 bg-amber-50/40 p-3"
           data-testid={`${testidPrefix}-exams-section`}>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-1.5">
            <FlaskConical className="h-4 w-4 text-amber-700" />
            <span className="font-semibold text-amber-900 text-sm">Esami / accertamenti da prescrivere</span>
          </div>
          <Button size="sm" variant="outline" onClick={suggestExams} disabled={aiBusy}
                  data-testid={`${testidPrefix}-exams-ai`}
                  className="border-purple-300 text-purple-700 hover:bg-purple-50 h-8 text-[11px]">
            <Sparkles className="h-3 w-3 mr-1" />
            {aiBusy ? "AI in analisi…" : "Suggerisci con AI"}
          </Button>
        </div>
        {examsRationale && (
          <div className="mb-2 rounded-md bg-white border border-amber-200 p-2 text-[11px] text-slate-700">
            <Sparkles className="h-3 w-3 inline text-purple-600 mr-1" />{examsRationale}
          </div>
        )}
        {aiSuggestions.length > 0 && (
          <div className="mb-2 rounded-lg border border-purple-200 bg-purple-50/60 p-2">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] font-semibold text-purple-800">Proposte AI ({aiSuggestions.length})</div>
              <Button size="sm" variant="ghost"
                      onClick={() => {
                        setExamLines([...examLines, ...aiSuggestions.map(s => ({ text: s.name, rationale: s.rationale, priority: s.priority }))]);
                        setAiSuggestions([]);
                        toast.success("Tutti gli esami aggiunti");
                      }}
                      className="text-purple-700 h-6 text-[10px]">Aggiungi tutti</Button>
            </div>
            <ul className="space-y-1">
              {aiSuggestions.map((s, i) => (
                <li key={i} className="rounded bg-white border border-purple-200 p-1.5 flex items-center gap-2">
                  <div className="flex-1 text-[12px]">
                    <span className="font-semibold">{s.name}</span>
                    {s.priority === "urgente" && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-red-100 text-red-700 font-bold">URGENTE</span>}
                    {s.rationale && <div className="text-[10px] text-slate-600">{s.rationale}</div>}
                  </div>
                  <Button size="sm" variant="outline"
                          onClick={() => {
                            setExamLines([...examLines, { text: s.name, rationale: s.rationale, priority: s.priority }]);
                            setAiSuggestions(aiSuggestions.filter((_, j) => j !== i));
                          }}
                          className="h-6 text-[10px] border-purple-300 text-purple-700">
                    <ListPlus className="h-3 w-3" />
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <SimpleList lines={examLines} setLines={setExamLines}
                    placeholder="Es. Emocromo con formula · ECG · Ecografia addome…"
                    testid={`${testidPrefix}-exams`} />
      </div>

      {/* ---- TERAPIA FARMACOLOGICA ---- */}
      <div className="rounded-xl border-2 border-purple-200 bg-purple-50/40 p-3"
           data-testid={`${testidPrefix}-rx-section`}>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-1.5">
            <Pill className="h-4 w-4 text-purple-700" />
            <span className="font-semibold text-purple-900 text-sm">Terapia farmacologica</span>
          </div>
          <Button size="sm" variant="outline" onClick={suggestRx} disabled={rxAiBusy}
                  data-testid={`${testidPrefix}-rx-ai`}
                  className="border-purple-300 text-purple-700 hover:bg-purple-50 h-8 text-[11px]">
            <Sparkles className="h-3 w-3 mr-1" />
            {rxAiBusy ? "AI in analisi…" : "Suggerisci con AI"}
          </Button>
        </div>
        {rxAiRationale && (
          <div className="mb-2 rounded-md bg-white border border-purple-200 p-2 text-[11px] text-slate-700">
            <Sparkles className="h-3 w-3 inline text-purple-600 mr-1" />{rxAiRationale}
          </div>
        )}
        {rxAiSuggestions.length > 0 && (
          <div className="mb-2 rounded-lg border border-purple-200 bg-purple-50/60 p-2">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] font-semibold text-purple-800">Proposte AI ({rxAiSuggestions.length})</div>
              <Button size="sm" variant="ghost"
                      onClick={() => {
                        setRxLines([...rxLines, ...rxAiSuggestions.map(s => ({
                          text: `${s.drug} — ${s.dose}${s.duration ? " × " + s.duration : ""}${s.indication ? " (" + s.indication + ")" : ""}`,
                          cautions: s.cautions,
                        }))]);
                        setRxAiSuggestions([]);
                        toast.success("Tutti i farmaci aggiunti");
                      }}
                      className="text-purple-700 h-6 text-[10px]">Aggiungi tutti</Button>
            </div>
            <ul className="space-y-1">
              {rxAiSuggestions.map((s, i) => (
                <li key={i} className="rounded bg-white border border-purple-200 p-1.5 flex items-center gap-2">
                  <div className="flex-1 text-[12px]">
                    <span className="font-semibold">{s.drug}</span> — <span className="text-slate-700">{s.dose}</span>
                    {s.duration && <span className="text-slate-600"> × {s.duration}</span>}
                    {s.indication && <div className="text-[10px] text-slate-600">{s.indication}</div>}
                    {s.cautions && <div className="text-[10px] text-amber-700"><AlertTriangle className="h-3 w-3 inline" /> {s.cautions}</div>}
                  </div>
                  <Button size="sm" variant="outline"
                          onClick={() => {
                            setRxLines([...rxLines, {
                              text: `${s.drug} — ${s.dose}${s.duration ? " × " + s.duration : ""}${s.indication ? " (" + s.indication + ")" : ""}`,
                              cautions: s.cautions,
                            }]);
                            setRxAiSuggestions(rxAiSuggestions.filter((_, j) => j !== i));
                          }}
                          className="h-6 text-[10px] border-purple-300 text-purple-700">
                    <ListPlus className="h-3 w-3" />
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <SimpleList lines={rxLines} setLines={setRxLines}
                    placeholder="Es. Ramipril 5 mg 1 cp x 30 gg · Paracetamolo 1 g al bisogno…"
                    testid={`${testidPrefix}-rx`} />
      </div>

      {/* ---- INDICAZIONI NON FARMACOLOGICHE ---- */}
      <div className="rounded-xl border-2 border-teal-200 bg-teal-50/40 p-3"
           data-testid={`${testidPrefix}-nonpharma-section`}>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-1.5">
            <Sprout className="h-4 w-4 text-teal-700" />
            <span className="font-semibold text-teal-900 text-sm">Indicazioni non farmacologiche</span>
            <span className="text-[10px] text-teal-700 italic">(stile di vita, dieta, esercizio, presidi, educazione al paziente)</span>
          </div>
          <Button size="sm" variant="outline" onClick={suggestNonPharma} disabled={nonPharmaBusy}
                  data-testid={`${testidPrefix}-nonpharma-ai`}
                  className="border-teal-400 text-teal-700 hover:bg-teal-50 h-8 text-[11px]">
            <Sparkles className="h-3 w-3 mr-1" />
            {nonPharmaBusy ? "AI in analisi…" : "Suggerisci con AI"}
          </Button>
        </div>
        <Textarea rows={4} value={mgmt.indicazioni_non_farmacologiche || ""}
                  onChange={(e) => setMgmt(s => ({ ...s, indicazioni_non_farmacologiche: e.target.value }))}
                  placeholder="Es. Dieta iposodica < 5 g/die, calo peso 5%, camminata 30' × 5/sett, sospensione alcol, monitoraggio PA domiciliare…"
                  data-testid={`${testidPrefix}-nonpharma-text`} />
        <div className="text-[10px] italic text-slate-500 mt-1">
          Il testo è modificabile: l'AI aggiunge le sue proposte, tu puoi correggere/personalizzare prima del salvataggio.
        </div>
      </div>

      {/* ---- INDICAZIONI AL PAZIENTE + CONTROLLO SUCCESSIVO ---- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[12px] flex items-center gap-1"><User className="h-3 w-3" /> Indicazioni al paziente</Label>
          <Textarea rows={3} value={mgmt.indicazioni}
                    onChange={(e) => setMgmt(s => ({ ...s, indicazioni: e.target.value }))}
                    placeholder="Cosa dire al paziente, precauzioni, sintomi di allarme che devono far tornare in visita…"
                    data-testid={`${testidPrefix}-indicazioni`} />
        </div>
        <div>
          <Label className="text-[12px] flex items-center gap-1"><Repeat className="h-3 w-3" /> Controllo successivo</Label>
          <Textarea rows={3} value={mgmt.controllo_successivo}
                    onChange={(e) => setMgmt(s => ({ ...s, controllo_successivo: e.target.value }))}
                    placeholder="Es. Rivalutazione in 2 settimane con esiti esami · controllo PA in 1 mese"
                    data-testid={`${testidPrefix}-controllo`} />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[12px]">Note</Label>
          <Textarea rows={2} value={mgmt.note}
                    onChange={(e) => setMgmt(s => ({ ...s, note: e.target.value }))}
                    data-testid={`${testidPrefix}-note`} />
        </div>
      </div>
    </div>
  );
}

function PathwayCard({ title, onBack, children }) {
  return (
    <Card className="p-4 rounded-2xl border-blue-300 border-2 shadow-md">
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-2.5 mb-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] text-blue-800">
          Hai dimenticato qualcosa (anagrafica, allergie, motivo…)? Torna alla schermata iniziale — i dati inseriti in questo percorso restano salvati.
        </div>
        <Button variant="outline" size="sm" onClick={onBack}
                data-testid="pathway-back"
                className="border-blue-400 text-blue-800 hover:bg-blue-100 h-8">
          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Torna alla schermata iniziale
        </Button>
      </div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-lg font-bold text-slate-900">{title}</h3>
      </div>
      {children}
    </Card>
  );
}

function DifferentialDxBox({ items, onDismiss, onCopyToEvaluation, selectedHyps, onSelect }) {
  const sevCls = {
    high: "border-red-300 bg-red-50",
    moderate: "border-amber-300 bg-amber-50",
    low: "border-emerald-300 bg-emerald-50",
  };
  const barCls = {
    high: "bg-red-500", moderate: "bg-amber-500", low: "bg-emerald-500",
  };
  const isSelected = (d) => (selectedHyps || []).some((h) =>
    h?.diagnosis === d.diagnosis && h?.source === "ai");
  const copyAll = () => {
    const text = items.map((d, i) => (
      `${i+1}. ${d.diagnosis} (~${d.probability}%) — ${d.rationale}\n   → ${d.next_steps}`
    )).join("\n");
    onCopyToEvaluation("Ipotesi diagnostiche differenziali (AI, da validare):\n" + text);
    // Scroll all'area valutazione + notifica
    setTimeout(() => {
      const el = document.querySelector('[data-testid="case-evaluation"]');
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus({ preventScroll: true });
      }
    }, 60);
  };
  return (
    <div className="mt-3 rounded-xl border-2 border-fuchsia-200 bg-fuchsia-50/40 p-3"
         data-testid="case-dx-box">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-fuchsia-700" />
          <div className="text-[12px] font-semibold text-fuchsia-800">
            Ipotesi diagnostiche AI ({items.length}) — clicca per selezionarne fino a 3
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(selectedHyps || []).length > 0 && (
            <span className="text-[11px] font-semibold text-fuchsia-700 bg-fuchsia-100 px-2 py-0.5 rounded-full"
                  data-testid="case-dx-selected-count">
              {(selectedHyps || []).length}/3 selezionate
            </span>
          )}
          <Button size="sm" variant="ghost" onClick={copyAll}
                  data-testid="case-dx-copy"
                  className="text-fuchsia-700 hover:bg-fuchsia-100 h-7 text-[11px]">
            Copia in Valutazione
          </Button>
          <button onClick={onDismiss}
                  className="text-slate-600 hover:text-slate-900 text-xs font-semibold underline underline-offset-2"
                  data-testid="case-dx-dismiss">Nascondi</button>
        </div>
      </div>
      <ol className="space-y-2" data-testid="case-dx-list">
        {items.map((d, i) => (
          <li key={i}
              onClick={() => onSelect && onSelect({
                diagnosis: d.diagnosis, source: "ai",
                probability: d.probability, severity: d.severity,
                rationale: d.rationale, next_steps: d.next_steps,
              })}
              className={`rounded-lg border-2 p-3 cursor-pointer transition ${sevCls[d.severity] || sevCls.moderate}
                ${isSelected(d) ? "ring-2 ring-fuchsia-500 ring-offset-1" : "hover:shadow"}`}
              data-testid={`case-dx-item-${i}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-500 font-mono">#{i+1}</span>
                  <span className="font-bold text-slate-900 text-sm">{d.diagnosis}</span>
                  {isSelected(d) && (
                    <Badge className="bg-fuchsia-600 text-white border-fuchsia-700 text-[10px]">
                      <Check className="h-3 w-3 mr-0.5" /> selezionata
                    </Badge>
                  )}
                </div>
                <div className="mt-1.5 text-[12px] text-slate-700">{d.rationale}</div>
                {d.next_steps && (
                  <div className="mt-1.5 text-[11px] text-slate-600">
                    <span className="font-semibold">Prossimi passi:</span> {d.next_steps}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end flex-shrink-0 w-24">
                <div className="text-lg font-bold text-slate-900 leading-none">{d.probability}%</div>
                <div className="mt-1 w-full h-3 rounded-full bg-white overflow-hidden ring-1 ring-slate-300">
                  <div className={`h-full ${barCls[d.severity] || barCls.moderate}`}
                       style={{ width: `${Math.max(3, d.probability)}%` }} />
                </div>
                <div className="mt-0.5 text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                  {d.severity === "high" ? "Severità alta" : d.severity === "low" ? "Severità bassa" : "Severità media"}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] italic text-slate-500">
          Assistenza AI. Non sostituisce la valutazione professionale. Clicca su una o più card (max 3): se ne selezioni più d'una, l'AI suggerirà esami che aiutano a <b>differenziare</b> le ipotesi.
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// Pannello rapido esami/prescrizioni (aperto dopo click su motivo)
// -----------------------------------------------------------------
function QuickListPanel({
  type,
  lines, setLines,
  aiSuggestions, setAiSuggestions,
  aiRationale,
  onAiSuggest, aiBusy,
  caseId, canCreate, createOrUpdateCase,
  examsRationale,
}) {
  const isRx = type === "prescription";
  const [pdfBusy, setPdfBusy] = useState(false);
  const [manualTxt, setManualTxt] = useState("");
  const [editIdx, setEditIdx] = useState(null);
  const [editTxt, setEditTxt] = useState("");

  const rationale = isRx ? aiRationale : examsRationale;
  const colors = isRx
    ? { border: "border-purple-300", bg: "bg-purple-50/40", title: "text-purple-800", btn: "bg-purple-600 hover:bg-purple-700" }
    : { border: "border-amber-300", bg: "bg-amber-50/40", title: "text-amber-800", btn: "bg-amber-600 hover:bg-amber-700" };

  const addItem = (text, meta = {}) => {
    if (!text.trim()) return;
    setLines([...lines, { text: text.trim(), ...meta }]);
  };
  const removeItem = (i) => setLines(lines.filter((_, j) => j !== i));
  const startEdit = (i) => { setEditIdx(i); setEditTxt(lines[i].text); };
  const commitEdit = () => {
    if (editIdx == null) return;
    const next = [...lines];
    next[editIdx] = { ...next[editIdx], text: editTxt.trim() || next[editIdx].text };
    setLines(next); setEditIdx(null); setEditTxt("");
  };
  const addAllFromAi = () => {
    const merged = [...lines,
      ...aiSuggestions.map((s) => (
        isRx
          ? {
              text: `${s.drug} — ${s.dose}${s.duration ? " × " + s.duration : ""}${s.indication ? " (" + s.indication + ")" : ""}`,
              cautions: s.cautions,
            }
          : { text: s.name, rationale: s.rationale, priority: s.priority }
      ))];
    setLines(merged); setAiSuggestions([]);
    toast.success("Tutte le proposte AI aggiunte");
  };
  const addOneFromAi = (i) => {
    const s = aiSuggestions[i];
    const item = isRx
      ? {
          text: `${s.drug} — ${s.dose}${s.duration ? " × " + s.duration : ""}${s.indication ? " (" + s.indication + ")" : ""}`,
          cautions: s.cautions,
        }
      : { text: s.name, rationale: s.rationale, priority: s.priority };
    setLines([...lines, item]);
    setAiSuggestions(aiSuggestions.filter((_, j) => j !== i));
  };

  const generatePdf = async () => {
    if (lines.length === 0) {
      toast.error(isRx ? "Aggiungi almeno un farmaco" : "Aggiungi almeno un esame");
      return;
    }
    setPdfBusy(true);
    try {
      const id = caseId || await createOrUpdateCase();
      if (!id) return;
      await api.put(`/cases/${id}`, isRx ? { prescription: lines } : { exam_requests: lines });
      const res = await api.post(`/cases/${id}/quick-list-pdf`, { type }, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${isRx ? "prescrizioni" : "esami"}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(isRx ? "PDF prescrizioni pronto" : "PDF esami pronto");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore generazione PDF");
    } finally { setPdfBusy(false); }
  };

  return (
    <div className={`mt-3 rounded-xl border-2 ${colors.border} ${colors.bg} p-3`}
         data-testid={`quick-panel-${type}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className={`text-[13px] font-bold ${colors.title} flex items-center gap-1.5`}>
          {isRx ? <Pill className="h-4 w-4" /> : <FlaskConical className="h-4 w-4" />}
          {isRx ? "Prescrizioni farmacologiche" : "Esami / accertamenti"}
        </div>
        <Button size="sm" variant="outline" onClick={onAiSuggest} disabled={aiBusy || !canCreate}
                data-testid={`quick-${type}-ai`}
                className="border-purple-300 text-purple-700 hover:bg-purple-50 h-8 text-[11px]">
          <Sparkles className="h-3 w-3 mr-1" />
          {aiBusy ? "AI in analisi…" : isRx ? "Suggerisci prescrizione (AI)" : "Suggerisci esami (AI)"}
        </Button>
      </div>

      {rationale && (
        <div className="mb-2 rounded-lg bg-white border border-purple-200 p-2"
             data-testid={`quick-${type}-rationale`}>
          <div className="text-[11px] font-bold text-purple-800 mb-0.5">
            {isRx ? "Perché l'AI propone questa terapia" : "Perché l'AI propone questi esami"}
          </div>
          <div className="text-[12px] text-slate-700">{rationale}</div>
        </div>
      )}

      {aiSuggestions.length > 0 && (
        <div className="mb-2 rounded-lg bg-white border border-purple-200 p-2"
             data-testid={`quick-${type}-ai-list`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold text-purple-800">
              Proposte AI ({aiSuggestions.length}) — clicca "Aggiungi" per inserirle
            </span>
            <Button size="sm" variant="ghost" onClick={addAllFromAi}
                    className="text-purple-700 hover:bg-purple-100 h-6 text-[10px]"
                    data-testid={`quick-${type}-ai-add-all`}>
              Aggiungi tutte
            </Button>
          </div>
          <ul className="space-y-1">
            {aiSuggestions.map((s, i) => (
              <li key={i} className="rounded bg-slate-50 border border-slate-200 p-1.5 flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold text-slate-900">
                    {isRx ? s.drug : s.name}
                    {!isRx && s.priority === "urgente" && (
                      <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">URGENTE</span>
                    )}
                  </div>
                  {isRx && (
                    <div className="text-[10.5px] text-slate-600">
                      Dose: {s.dose}{s.duration ? ` · Durata: ${s.duration}` : ""}
                      {s.indication ? ` · ${s.indication}` : ""}
                    </div>
                  )}
                  {!isRx && s.rationale && (
                    <div className="text-[10.5px] text-slate-600">{s.rationale}</div>
                  )}
                  {isRx && s.cautions && (
                    <div className="text-[10px] text-amber-700 mt-0.5">
                      <AlertTriangle className="h-2.5 w-2.5 inline mr-0.5" />{s.cautions}
                    </div>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={() => addOneFromAi(i)}
                        className="h-6 text-[10px] border-purple-300 text-purple-700 shrink-0"
                        data-testid={`quick-${type}-ai-add-${i}`}>
                  <ListPlus className="h-3 w-3 mr-0.5" /> Aggiungi
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-lg bg-white border border-slate-200 p-2">
        <div className="text-[11px] font-bold text-slate-700 mb-1.5">
          {isRx ? "Lista prescrizioni (modificabile)" : "Lista esami (modificabile)"}
        </div>
        {lines.length === 0 ? (
          <div className="text-[11px] italic text-slate-400 py-2 text-center">Nessuna voce inserita.</div>
        ) : (
          <ul className="space-y-1 mb-2" data-testid={`quick-${type}-lines`}>
            {lines.map((l, i) => (
              <li key={i} className="flex items-center gap-1.5 rounded bg-slate-50 border border-slate-200 px-2 py-1"
                  data-testid={`quick-${type}-line-${i}`}>
                {editIdx === i ? (
                  <>
                    <Input value={editTxt} onChange={(e) => setEditTxt(e.target.value)}
                           onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitEdit(); } }}
                           className="h-7 text-[12px]" autoFocus />
                    <Button size="sm" onClick={commitEdit} className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white">
                      Salva
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-[12px] text-slate-800">{l.text}</span>
                    <button type="button" onClick={() => startEdit(i)}
                            className="text-[10px] text-blue-600 hover:underline">Modifica</button>
                    <button type="button" onClick={() => removeItem(i)}
                            className="text-[10px] text-red-500 hover:underline">Rimuovi</button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-1.5">
          <Input value={manualTxt} onChange={(e) => setManualTxt(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(manualTxt); setManualTxt(""); } }}
                 placeholder={isRx
                   ? "Aggiungi manualmente. Es. Ramipril 5 mg 1 cp/die × 30 gg"
                   : "Aggiungi manualmente. Es. Emocromo completo con formula"}
                 className="h-8 text-[12px] flex-1"
                 data-testid={`quick-${type}-manual`} />
          <Button size="sm" variant="outline"
                  onClick={() => { addItem(manualTxt); setManualTxt(""); }}
                  disabled={!manualTxt.trim()}
                  data-testid={`quick-${type}-manual-add`}
                  className="h-8 text-[11px]">
            <ListPlus className="h-3 w-3 mr-1" /> Aggiungi
          </Button>
        </div>
      </div>

      <div className="mt-3 flex justify-end">
        <Button onClick={generatePdf} disabled={pdfBusy || lines.length === 0 || !canCreate}
                data-testid={`quick-${type}-pdf`}
                className={`${colors.btn} text-white`}>
          <FileText className="h-4 w-4 mr-2" />
          {pdfBusy ? "Genero PDF…"
            : isRx ? "Genera PDF prescrizioni (per segretaria)"
            : "Genera PDF esami (per segretaria)"}
        </Button>
      </div>
      <div className="text-[10px] italic text-slate-500 mt-1 text-right">
        Il PDF firmato viene consegnato al paziente per l'emissione della ricetta in studio.
      </div>
    </div>
  );
}

function SimpleList({ lines, setLines, placeholder, testid }) {
  const [txt, setTxt] = useState("");
  const add = () => { if (txt.trim()) { setLines([...lines, { text: txt.trim() }]); setTxt(""); } };
  return (
    <div>
      <div className="flex gap-2">
        <Input value={txt} onChange={(e) => setTxt(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
               placeholder={placeholder} data-testid={`${testid}-input`} className="h-10" />
        <Button onClick={add} type="button" variant="outline"
                data-testid={`${testid}-add`} className="h-10 border-slate-200">
          <ListPlus className="h-4 w-4 mr-1.5" /> Aggiungi
        </Button>
      </div>
      {lines.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid={`${testid}-list`}>
          {lines.map((l, i) => (
            <li key={i} className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-sm">
              <span>{l.text}</span>
              <button onClick={() => setLines(lines.filter((_, j) => j !== i))}
                      className="text-slate-400 hover:text-red-600 text-xs">Rimuovi</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// Moduli raggruppati per specializzazione con icona circolare
// -----------------------------------------------------------------
const SPEC_META = {
  "Cardiologia":               { Icon: Heart,       cls: "bg-red-100 text-red-700 ring-red-200" },
  "Neurologia":                { Icon: Brain,       cls: "bg-indigo-100 text-indigo-700 ring-indigo-200" },
  "Pneumologia":               { Icon: Wind,        cls: "bg-sky-100 text-sky-700 ring-sky-200" },
  "Ortopedia":                 { Icon: Bone,        cls: "bg-amber-100 text-amber-700 ring-amber-200" },
  "Ortopedia e Traumatologia": { Icon: Bone,        cls: "bg-amber-100 text-amber-700 ring-amber-200" },
  "Otorinolaringoiatria":      { Icon: Ear,         cls: "bg-orange-100 text-orange-700 ring-orange-200" },
  "Gastroenterologia":         { Icon: Utensils,    cls: "bg-emerald-100 text-emerald-700 ring-emerald-200" },
  "Psichiatria":               { Icon: Puzzle,      cls: "bg-fuchsia-100 text-fuchsia-700 ring-fuchsia-200" },
  "Oculistica":                { Icon: Eye,         cls: "bg-blue-100 text-blue-700 ring-blue-200" },
  "Oftalmologia":              { Icon: Eye,         cls: "bg-blue-100 text-blue-700 ring-blue-200" },
  "Medicina Interna":          { Icon: Stethoscope, cls: "bg-teal-100 text-teal-700 ring-teal-200" },
  "Endocrinologia":            { Icon: Sprout,      cls: "bg-lime-100 text-lime-700 ring-lime-200" },
  "Ginecologia":               { Icon: Ribbon,      cls: "bg-pink-100 text-pink-700 ring-pink-200" },
  "Pediatria":                 { Icon: Baby,        cls: "bg-cyan-100 text-cyan-700 ring-cyan-200" },
  "Dermatologia":              { Icon: Sun,         cls: "bg-orange-100 text-orange-700 ring-orange-200" },
  "Urologia":                  { Icon: Droplets,    cls: "bg-yellow-100 text-yellow-700 ring-yellow-200" },
  "Reumatologia":              { Icon: HandMetal,   cls: "bg-violet-100 text-violet-700 ring-violet-200" },
  "Infettivologia":            { Icon: Beaker,      cls: "bg-teal-100 text-teal-700 ring-teal-200" },
  "Nefrologia":                { Icon: Droplets,    cls: "bg-yellow-100 text-yellow-700 ring-yellow-200" },
  "Ematologia":                { Icon: Activity,    cls: "bg-rose-100 text-rose-700 ring-rose-200" },
  "Oncologia":                 { Icon: Activity,    cls: "bg-rose-100 text-rose-700 ring-rose-200" },
  "Neurochirurgia":            { Icon: Zap,         cls: "bg-purple-100 text-purple-700 ring-purple-200" },
};

function ModulesBySpecialization({ modules, selected, onPick }) {
  const grouped = useMemo(() => {
    const g = {};
    (modules || []).forEach(m => {
      const s = m.suggested_specialization || "Altro";
      (g[s] = g[s] || []).push(m);
    });
    return Object.entries(g).sort(([a],[b]) => a.localeCompare(b));
  }, [modules]);
  if (!modules?.length) {
    return <div className="text-[11px] text-slate-500 italic">Caricamento moduli…</div>;
  }
  return (
    <div className="space-y-3">
      {grouped.map(([spec, mods]) => {
        const meta = SPEC_META[spec] || { Icon: Stethoscope, cls: "bg-slate-100 text-slate-700 ring-slate-200" };
        const Icon = meta.Icon;
        return (
          <div key={spec} data-testid={`case-spec-group-${spec.toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`h-9 w-9 rounded-full flex items-center justify-center ring-2 ${meta.cls}`}>
                <Icon className="h-5 w-5" aria-hidden />
              </span>
              <span className="text-[13px] font-bold text-slate-800">{spec}</span>
            </div>
            <div className="flex gap-1.5 flex-wrap pl-11">
              {mods.map(m => (
                <button key={m.id} type="button"
                        onClick={() => onPick(m.id)}
                        data-testid={`case-module-${m.id}`}
                        className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border-2 transition
                          ${selected === m.id ? "border-rose-500 bg-rose-100 text-rose-800" : "border-slate-200 bg-white text-slate-700 hover:border-rose-300"}`}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        );
      })}
      {/* Altro — nessun modulo copre il caso */}
      <div className="pt-2 border-t border-slate-200">
        <button type="button" onClick={() => onPick("__altro")}
                data-testid="case-module-altro"
                className="w-full flex items-center gap-3 rounded-xl border-2 border-dashed border-slate-300 bg-white px-3 py-2 hover:border-slate-500 transition text-left">
          <span className="h-9 w-9 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center ring-2 ring-slate-200">
            <Plus className="h-5 w-5" aria-hidden />
          </span>
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-slate-800">Altro — nessun modulo copre il caso</div>
            <div className="text-[11px] text-slate-500">Usa il testo libero sopra e l'AI ti aiuterà a orientarti</div>
          </div>
        </button>
      </div>
    </div>
  );
}
